import { getToken, onMessage, isSupported } from "firebase/messaging";
import { captureException, captureMessage } from "shared/sentry";
import { resolveMessaging } from "../config/firebase";
import { API_BASE_URL } from "../constants/api";
import { AUTH_TOKEN_KEYS } from "../constants/common";
import { getLocalItem } from "./config/localStorage";
import { PUSH_CONFIG } from "../pusNotification/fcm/pushConstants";
import {
  buildFcmServerSyncId,
  clearStoredFcmTokens,
  getStoredFcmTokens,
  saveFcmServerSyncId,
  saveLocalTokenIfChanged,
} from "../pusNotification/fcm/fcmStorage";
import { dispatchForegroundPushEvent } from "../pusNotification/fcm/pushEvents";
import {
  hasFirebaseMessagingDatabase,
  waitForFcmEnvironmentReady,
  waitForFirebaseMessagingDatabase,
  waitForIndexedDBReady,
} from "../pusNotification/fcm/firebaseIdbReady";
import {
  isSafariBrowser,
  resolvePushPermissionState,
} from "../pusNotification/fcm/pushPermission";
import {
  flattenPushDataPayload,
  resolvePushLink,
} from "../pusNotification/fcm/pushPayloadUtils";
import {
  resolveFcmNotificationBody,
  resolveFcmNotificationTitle,
  resolvePushNotificationIcon,
  resolvePushNotificationTag,
} from "../pusNotification/fcm/pushNotificationPresentation";
import { shouldSuppressVisibleIncomingCallPush } from "../pusNotification/fcm/pushCallNotificationPolicy";
import { isLoginWithSupportPassword } from "./function";

const FIREBASE_SW_PATH = '/firebase-messaging-sw.js';
const DEFAULT_NOTIFICATION_TITLE = PUSH_CONFIG.DEFAULT_TITLE;
const DEFAULT_NOTIFICATION_LINK = PUSH_CONFIG.DEFAULT_LINK;
const PUSH_ERROR_TAGS = { area: "push-notification" };
const PUSH_STEPS = {
  REGISTER_SW: "register-service-worker",
  GET_TOKEN: "get-token",
  INITIALIZE_TOKEN: "initialize-token",
  SYNC_TOKEN: "sync-token-with-server",
  LISTEN_MESSAGES: "listen-for-messages",
};

const getVapidKey = () =>
  process.env.REACT_APP_FIREBASE_VAPID_KEY || process.env.REACT_APP_VAPID_KEY || "";

const extractDataPayload = (payload) => flattenPushDataPayload(payload);

const logWarn = (message, error) => {
  if (error) {
    console.warn(message, error);
    return;
  }
  console.warn(message);
};

const capturePushError = (error, context = {}) => {
  if (!error) return;
  captureException(error, {
    tags: PUSH_ERROR_TAGS,
    extra: context,
  });
};

const capturePushMessage = (message, context = {}) => {
  captureMessage(message, {
    level: "warning",
    tags: PUSH_ERROR_TAGS,
    extra: context,
  });
};

const resolveNotificationLink = (payload) => resolvePushLink(payload, DEFAULT_NOTIFICATION_LINK);

const resolveNotificationTag = (payload, data) =>
  resolvePushNotificationTag(payload, data, 'juicyhouse-foreground');

const getCurrentConfigSignature = () =>
  JSON.stringify({
    apiBaseUrl: API_BASE_URL,
    vapidKey: getVapidKey(),
    senderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
    userId: getLocalItem(AUTH_TOKEN_KEYS.USER_ID, ""),
  });

let swRegistrationPromise = null;

export const resetFirebaseServiceWorkerRegistration = () => {
  swRegistrationPromise = null;
};

export const resetFirebasePushServiceWorkers = async () => {
  resetFirebaseServiceWorkerRegistration();
  await cleanupStaleFirebaseServiceWorkers({ removeAll: true });
};

const FIREBASE_SW_SCRIPT = 'firebase-messaging-sw.js';

const getServiceWorkerScriptUrl = (worker) => worker?.scriptURL || '';

const isCanonicalFirebaseServiceWorker = (scriptUrl) => {
  if (!scriptUrl) {
    return false;
  }
  try {
    const url = new URL(scriptUrl);
    return url.pathname.endsWith(`/${FIREBASE_SW_SCRIPT}`) && !url.search;
  } catch {
    return false;
  }
};

const isFirebaseMessagingServiceWorker = (scriptUrl) =>
  scriptUrl.includes(FIREBASE_SW_SCRIPT);

/** Remove versioned (`?v=`) and duplicate Firebase push workers that block getToken on Chrome. */
const cleanupStaleFirebaseServiceWorkers = async ({ removeAll = false } = {}) => {
  if (!navigator.serviceWorker?.getRegistrations) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    registrations.map(async (registration) => {
      const scriptUrls = [registration.active, registration.waiting, registration.installing]
        .map(getServiceWorkerScriptUrl)
        .filter(Boolean);

      const hasFirebaseScript = scriptUrls.some(isFirebaseMessagingServiceWorker);
      const shouldRemove =
        registration.scope.includes("firebase-cloud-messaging-push-scope") ||
        (removeAll && hasFirebaseScript) ||
        scriptUrls.some(
          (url) =>
            isFirebaseMessagingServiceWorker(url) &&
            !isCanonicalFirebaseServiceWorker(url)
        );

      if (shouldRemove) {
        await registration.unregister();
      }
    })
  );
};

const findCanonicalFirebaseServiceWorkerRegistration = async () => {
  if (!navigator.serviceWorker?.getRegistrations) {
    return null;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  return (
    registrations.find(
      (registration) =>
        registration.active &&
        isCanonicalFirebaseServiceWorker(getServiceWorkerScriptUrl(registration.active))
    ) || null
  );
};

const waitForServiceWorkerActive = (registration, timeoutMs = 20000) =>
  new Promise((resolve) => {
    if (registration?.active) {
      resolve(registration);
      return;
    }

    const worker = registration?.installing || registration?.waiting;
    if (!worker) {
      resolve(registration);
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      worker.removeEventListener("statechange", onStateChange);
      resolve(registration);
    };

    const onStateChange = () => {
      if (registration.active) {
        finish();
      }
    };

    worker.addEventListener("statechange", onStateChange);
    window.setTimeout(finish, timeoutMs);
  });

const registerFirebaseServiceWorker = async () => {
  if (typeof navigator === "undefined" || !navigator.serviceWorker) {
    return null;
  }

  if (!swRegistrationPromise) {
    swRegistrationPromise = (async () => {
      try {
        await cleanupStaleFirebaseServiceWorkers();

        let registration = await findCanonicalFirebaseServiceWorkerRegistration();
        if (!registration?.active) {
          registration = await navigator.serviceWorker.register(FIREBASE_SW_PATH, {
            updateViaCache: "none",
          });
        } else {
          await registration.update().catch(() => {});
        }

        await waitForServiceWorkerActive(registration);
        await navigator.serviceWorker.ready;

        if (!registration.active) {
          logWarn("pushNotification >> Firebase service worker is not active after registration.");
          return null;
        }

        return registration;
      } catch (error) {
        swRegistrationPromise = null;
        capturePushError(error, {
          step: PUSH_STEPS.REGISTER_SW,
          serviceWorkerPath: FIREBASE_SW_PATH,
        });
        return null;
      }
    })();
  }

  return swRegistrationPromise;
};

const handleTokenError = (error) => {
  const code = error?.code;
  if (code === "messaging/permission-blocked") {
    return logWarn("pushNotification >> Notification permission was blocked by the user.");
  }
  if (code === "messaging/permission-default") {
    return logWarn("pushNotification >> Notification permission is still in default state.");
  }
  if (code === "messaging/unsupported-browser") {
    return logWarn("Browser does not support Firebase Messaging.");
  }
  capturePushError(error, { step: PUSH_STEPS.GET_TOKEN });
  return logWarn("pushNotification >> Error getting FCM token.", error);
};

const getActiveMessaging = () => resolveMessaging();

const getFcmToken = async () => {
  try {
    const messagingSupported = await isSupported();
    if (!messagingSupported) {
      logWarn("pushNotification >> Firebase Messaging is not supported in this browser.");
      capturePushMessage(
        "Firebase Messaging is not supported in this browser/environment.",
        { step: PUSH_STEPS.GET_TOKEN }
      );
      return null;
    }

    let permission = await resolvePushPermissionState();
    if (
      permission === "granted" &&
      typeof Notification !== "undefined" &&
      Notification.permission !== "granted"
    ) {
      if (isSafariBrowser()) {
        try {
          permission = await Notification.requestPermission();
        } catch (_) {
          // ignore — fall through to permission check below
        }
      } else {
        permission = Notification.permission;
      }
    }
    if (permission !== "granted") {
      logWarn(
        `pushNotification >> Notification permission is "${permission}" (Notification.permission="${typeof Notification !== "undefined" ? Notification.permission : "n/a"}").`
      );
      return null;
    }

    await waitForIndexedDBReady();

    const serviceWorkerRegistration = await registerFirebaseServiceWorker();
    if (!serviceWorkerRegistration?.active) {
      logWarn("pushNotification >> Firebase service worker is not active.");
      return null;
    }

    await waitForFcmEnvironmentReady();

    let activeMessaging = resolveMessaging();
    if (!activeMessaging) {
      await waitForIndexedDBReady({ timeoutMs: 8000, pollMs: 150 });
      activeMessaging = resolveMessaging();
    }
    if (!activeMessaging) {
      logWarn("pushNotification >> Firebase messaging instance is unavailable.");
      return null;
    }

    const vapidKey = getVapidKey();
    if (!vapidKey) {
      logWarn("pushNotification >> Missing VAPID key.");
      capturePushMessage(
        "Missing VAPID key. Set REACT_APP_VAPID_KEY (or REACT_APP_FIREBASE_VAPID_KEY).",
        { step: PUSH_STEPS.GET_TOKEN }
      );
      return null;
    }

    const maxAttempts = isSafariBrowser()
      ? FCM_TOKEN_ATTEMPTS_SAFARI
      : FCM_TOKEN_ATTEMPTS_DEFAULT;
    let lastTokenError = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        await waitForIndexedDBReady({
          timeoutMs: isSafariBrowser() ? 4000 : 3000,
          pollMs: 150,
        });
        if (!(await hasFirebaseMessagingDatabase())) {
          await waitForFirebaseMessagingDatabase({
            timeoutMs: isSafariBrowser() ? 5000 : 3000,
            pollMs: 200,
          });
        }
        await sleep(FCM_TOKEN_RETRY_BASE_MS * 2 ** (attempt - 1));
      }

      try {
        resolveMessaging();
        const token = await fetchFcmTokenFromMessaging(
          serviceWorkerRegistration,
          vapidKey
        );
        if (token) {
          if (process.env.NODE_ENV === "development") {
            console.log("pushNotification >> FCM token obtained.");
          }
          return token;
        }
      } catch (tokenError) {
        lastTokenError = tokenError;
        const retryable =
          isIndexedDbRelatedError(tokenError) ||
          isSafariBrowser() ||
          !(await hasFirebaseMessagingDatabase());
        if (!retryable) {
          handleTokenError(tokenError);
          return null;
        }
      }
    }

    if (lastTokenError) {
      handleTokenError(lastTokenError);
    } else {
      logWarn(
        "pushNotification >> No FCM token returned. Check service worker and Firebase config."
      );
      capturePushMessage(
        "No FCM token available. Ensure Firebase configuration and service worker registration are correct.",
        { step: PUSH_STEPS.GET_TOKEN }
      );
    }
    return null;
  } catch (err) {
    // messaging/unsupported-browser is an expected, benign outcome for in-app browsers
    // (Instagram WKWebView, etc.). Suppress it from Sentry to avoid noise.
    if (err?.code === 'messaging/unsupported-browser') {
      logWarn("Browser does not support Firebase Messaging.");
      return null;
    }
    capturePushError(err, { step: PUSH_STEPS.INITIALIZE_TOKEN });
    logWarn("pushNotification >> FCM token initialization failed.", err);
    return null;
  }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const FCM_TOKEN_ATTEMPTS_DEFAULT = 5;
const FCM_TOKEN_ATTEMPTS_SAFARI = 6;
const FCM_TOKEN_RETRY_BASE_MS = 300;

const isIndexedDbRelatedError = (error) => {
  const message = `${error?.message || ""} ${error?.code || ""}`.toLowerCase();
  return (
    message.includes("indexeddb") ||
    message.includes("idb") ||
    message.includes("database") ||
    message.includes("storage")
  );
};

const fetchFcmTokenFromMessaging = async (serviceWorkerRegistration, vapidKey) => {
  const activeMessaging = getActiveMessaging();
  if (!activeMessaging) {
    return null;
  }

  const token = await getToken(activeMessaging, {
    vapidKey,
    serviceWorkerRegistration,
  });

  return token || null;
};

const syncTokenWithServerRetry = async (updateFcmToken, latestToken) => {
  const max = PUSH_CONFIG.TOKEN_SYNC_MAX_RETRIES;
  for (let attempt = 0; attempt < max; attempt++) {
    try {
      const response = await updateFcmToken(latestToken);
      if (response?.success) {
        return latestToken;
      }
    } catch (error) {
      capturePushError(error, {
        step: PUSH_STEPS.SYNC_TOKEN,
        attempt: attempt + 1,
        maxAttempts: max,
      });
    }
    if (attempt < max - 1) {
      await sleep(PUSH_CONFIG.TOKEN_SYNC_RETRY_BASE_MS * 2 ** attempt);
    }
  }
  return null;
};

/** Dev-only direct token fetch (surfaces Firebase errors in the console). */
export const debugFetchFcmToken = () => getFcmToken();

export const syncFcmTokenWithServer = async ({
  isUserEligible,
  updateFcmToken,
  getCachedToken = getStoredFcmTokens,
  forcePatch = false,
}) => {
  if (typeof isUserEligible === "function" && !isUserEligible()) {
    return null;
  }

  if (typeof updateFcmToken !== "function") {
    capturePushMessage("syncFcmTokenWithServer requires updateFcmToken callback.", {
      step: PUSH_STEPS.SYNC_TOKEN,
    });
    return null;
  }

  try {
    const { localToken, tokenConfigSignature, serverSyncId } = getCachedToken();
    const currentConfigSignature = getCurrentConfigSignature();
    const isTokenConfigChanged =
      tokenConfigSignature && tokenConfigSignature !== currentConfigSignature;

    if (isTokenConfigChanged) {
      clearStoredFcmTokens();
    }

    const latestToken = await getFcmToken();
    if (!latestToken) {
      return null;
    }

    saveLocalTokenIfChanged(localToken, latestToken, getCurrentConfigSignature);

    const nextServerSyncId = buildFcmServerSyncId(latestToken, currentConfigSignature);
    const lastServerSyncId = isTokenConfigChanged ? null : serverSyncId;
    if (!forcePatch && lastServerSyncId === nextServerSyncId) {
      return latestToken;
    }

    const syncedToken = await syncTokenWithServerRetry(updateFcmToken, latestToken);
    if (syncedToken) {
      saveFcmServerSyncId(nextServerSyncId);
      return syncedToken;
    }

    // Token is valid locally even when the PATCH fails (offline / transient API errors).
    return latestToken;
  } catch (error) {
    capturePushError(error, { step: PUSH_STEPS.SYNC_TOKEN });
    return null;
  }
};

export const handleIncomingFcmPayload = (payload) => {
  if (isLoginWithSupportPassword()) {
    return;
  }

  const data = extractDataPayload(payload);
  const title = resolveFcmNotificationTitle(payload, data, DEFAULT_NOTIFICATION_TITLE);
  const body = resolveFcmNotificationBody(payload, data);
  const targetLink = resolveNotificationLink(payload);
  const notificationId = data?.notificationId || data?.notification_id || payload?.messageId;

  if (typeof document !== "undefined" && document.visibilityState === "visible") {
    if (shouldSuppressVisibleIncomingCallPush(data)) {
      return;
    }
    dispatchForegroundPushEvent({
      title,
      body,
      link: targetLink,
      icon: resolvePushNotificationIcon(payload, data),
      tag: resolveNotificationTag(payload, data),
      notificationId,
      data,
    });
    return;
  }

  // Background banners are owned by the service worker / FCM SDK. Showing another
  // OS notification here duplicates the lock-screen banner on iOS.
};

/** @returns {(() => void) | null} unsubscribe, or null when messaging is unavailable */
export const listenForMessages = (onPayloadHandled) => {
  const activeMessaging = getActiveMessaging();
  if (!activeMessaging) {
    return null;
  }

  try {
    return onMessage(activeMessaging, (payload) => {
      if (process.env.NODE_ENV === "development") {
        console.log("pushNotification >> fcm", payload);
      }

      try {
        handleIncomingFcmPayload(payload);
        if (typeof onPayloadHandled === "function") {
          onPayloadHandled(payload);
        }
      } catch (error) {
        capturePushError(error, { step: PUSH_STEPS.LISTEN_MESSAGES, phase: "onMessage-handler" });
      }
    });
  } catch (error) {
    if (error?.code === 'messaging/unsupported-browser') {
      logWarn("Browser does not support Firebase Messaging.");
      return null;
    }
    capturePushError(error, { step: PUSH_STEPS.LISTEN_MESSAGES });
    return null;
  }
};
